import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { WardrobeImg } from "@/components/WardrobeImg";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";
import type { WardrobeItem } from "@/components/Wardrobe";

interface EditItemSheetProps {
  item: WardrobeItem | null;
  onClose: () => void;
  onSave: (updates: { name: string; category: string; size_purchased: string | null }) => Promise<void>;
  onDelete: () => Promise<void>;
}

const CATEGORY_OPTIONS: { label: string; value: string }[] = [
  { label: "Tops", value: "top" },
  { label: "Bottoms", value: "bottom" },
  { label: "Dresses", value: "dress" },
  { label: "Outerwear", value: "outerwear" },
  { label: "Shoes", value: "shoes" },
  { label: "Bags", value: "bag" },
  { label: "Accessories", value: "accessory" },
  { label: "Other", value: "other" },
];

const SIZE_PLACEHOLDERS: Record<string, string> = {
  top: "e.g. M, L, XL",
  bottom: "e.g. 32x32 or W32 L30",
  dress: "e.g. UK 10",
  outerwear: "e.g. M, L",
  shoes: "e.g. UK 6, EU 39",
  bag: "Optional",
  accessory: "Optional",
  other: "Optional",
};

const EYEBROW: React.CSSProperties = {
  color: "#9B958E",
};

const INPUT_BASE: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  background: "#FFFCF8",
  borderRadius: 12,
  fontSize: 16,
  color: "#2C2820",
  boxSizing: "border-box",
  outline: "none",
};

export function EditItemSheet({ item, onClose, onSave, onDelete }: EditItemSheetProps) {
  const [localName, setLocalName] = useState("");
  const [localCategory, setLocalCategory] = useState("");
  const [localSize, setLocalSize] = useState("");
  const [nameFocused, setNameFocused] = useState(false);
  const [sizeFocused, setSizeFocused] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const originalSize = (item as any)?.size_purchased ?? "";

  useEffect(() => {
    if (item) {
      setLocalName(item.name ?? "");
      setLocalCategory(item.category ?? "other");
      setLocalSize(originalSize ?? "");
      setShowDeleteConfirm(false);
      setNameFocused(false);
      setSizeFocused(false);
      trackEvent("wardrobe_item_edit_opened", { item_id: item.id });
    }
  }, [item?.id]);

  const dirty = useMemo(() => {
    if (!item) return false;
    const trimmedName = localName.trim();
    if (trimmedName === "") return false;
    const trimmedSize = localSize.trim();
    const origSizeNorm = (originalSize ?? "").trim();
    return (
      trimmedName !== (item.name ?? "") ||
      localCategory !== item.category ||
      trimmedSize !== origSizeNorm
    );
  }, [item, localName, localCategory, localSize, originalSize]);

  if (!item) return null;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await triggerHaptic("success");
      const trimmedSize = localSize.trim();
      await onSave({
        name: localName.trim(),
        category: localCategory,
        size_purchased: trimmedSize === "" ? null : trimmedSize,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await triggerHaptic("primary-action");
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-end justify-center"
        onClick={onClose}
        style={{
          background: "rgba(44,40,32,0.4)",
          paddingBottom: "calc(78px + var(--safe-bottom, 0px))",
        }}
      >
        <div
          className="relative w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#FFFCF8",
            borderRadius: "22px 22px 0 0",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {/* Drag handle */}
          <div style={{ padding: "8px 0", display: "flex", justifyContent: "center" }}>
            <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
          </div>

          {/* Header */}
          <div
            style={{
              padding: "0 16px 14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div className="l2f-type-section-header" style={EYEBROW}>EDIT ITEM</div>
              <div
                style={{
                  fontSize: 18,
                  fontStyle: "italic",
                  color: "#2C2820",
                  fontFamily: "var(--font-serif)",
                  lineHeight: 1.1,
                  marginTop: 1,
                }}
              >
                Your details
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#F1EFE8",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
              aria-label="Close"
            >
              <X size={14} color="#4A4540" />
            </button>
          </div>

          {/* Item preview */}
          <div
            style={{
              margin: "0 16px 16px",
              padding: "12px 14px",
              background: "#F1EFE8",
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                background: item.color_hex ?? "#E0D8D0",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <WardrobeImg item={item} className="w-full h-full object-cover" />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontStyle: "italic",
                color: "#2C2820",
                fontFamily: "var(--font-serif)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.name}
            </div>
          </div>

          {/* Name */}
          <div className="l2f-type-section-header" style={{ ...EYEBROW, padding: "0 16px 6px" }}>NAME</div>
          <div style={{ padding: "0 16px 14px" }}>
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              style={{
                ...INPUT_BASE,
                border: nameFocused ? "1.5px solid #C84B2F" : "1px solid #E0D8D0",
              }}
            />
          </div>

          {/* Category */}
          <div className="l2f-type-section-header" style={{ ...EYEBROW, padding: "0 16px 6px" }}>CATEGORY</div>
          <div style={{ padding: "0 16px 14px" }}>
            <div
              style={{
                display: "flex",
                gap: 6,
                overflowX: "auto",
                paddingBottom: 4,
                scrollbarWidth: "none",
              }}
              className="hide-scrollbar"
            >
              {CATEGORY_OPTIONS.map((opt) => {
                const active = localCategory === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setLocalCategory(opt.value)}
                    style={{
                      display: "inline-block",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      borderRadius: 22,
                      padding: "5px 11px",
                      fontSize: 11,
                      background: active ? "#2C2820" : "#FFFCF8",
                      color: active ? "#F5F0EB" : "#4A4540",
                      border: active ? "1px solid transparent" : "1px solid rgba(0,0,0,0.07)",
                      cursor: "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Size */}
          <div className="l2f-type-section-header" style={{ ...EYEBROW, padding: "0 16px 6px" }}>SIZE (OPTIONAL)</div>
          <div style={{ padding: "0 16px 14px" }}>
            <input
              type="text"
              value={localSize}
              onChange={(e) => setLocalSize(e.target.value)}
              onFocus={() => setSizeFocused(true)}
              onBlur={() => setSizeFocused(false)}
              placeholder={SIZE_PLACEHOLDERS[localCategory] ?? "Optional"}
              style={{
                ...INPUT_BASE,
                border: sizeFocused ? "1.5px solid #C84B2F" : "1px solid #E0D8D0",
              }}
            />
          </div>

          {/* Save */}
          <div style={{ padding: "4px 16px 12px" }}>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                width: "100%",
                padding: 14,
                minHeight: 44,
                border: "none",
                borderRadius: 22,
                fontSize: 15,
                fontWeight: 500,
                background: dirty ? "#C84B2F" : "#D3D1C7",
                color: dirty ? "#fff" : "#9B958E",
                cursor: dirty && !saving ? "pointer" : "default",
              }}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          <div style={{ borderTop: "0.5px solid #F1EFE8" }} />

          {/* Delete */}
          <div style={{ padding: "14px 16px 18px", textAlign: "center" }}>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                background: "none",
                border: "none",
                padding: "12px 16px",
                minHeight: 44,
                fontSize: 12,
                color: "#C84B2F",
                textDecoration: "underline",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Delete this item
            </button>
            <div className="l2f-type-caption" style={{ color: "#9B958E", marginTop: 4 }}>
              This can't be undone.
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(44,40,32,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            zIndex: 60,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FFFCF8",
              borderRadius: 20,
              padding: 22,
              maxWidth: 280,
              width: "100%",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontStyle: "italic",
                color: "#C84B2F",
                fontFamily: "var(--font-serif)",
                lineHeight: 1.2,
              }}
            >
              Delete this item?
            </div>
            <div className="l2f-type-caption" style={{ color: "#6B6560", lineHeight: 1.5, marginTop: 8 }}>
              "{item.name}" will be removed from your wardrobe. You can't undo this.
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  padding: 14,
                  minHeight: 44,
                  background: "transparent",
                  border: "1px solid rgba(0,0,0,0.15)",
                  color: "#4A4540",
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: 14,
                  minHeight: 44,
                  background: "#C84B2F",
                  color: "#fff",
                  border: "none",
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: deleting ? "default" : "pointer",
                }}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
